<template>
    <div class="timelineClip" :class="{ selected }" :style="previewStyle" @click="$emit('select')">
        <div class="tc-thumb" :class="{ 'tc-thumb-reviewing': previewSegments }">
            <video
                v-if="clip.sourceType === 'video'"
                ref="probe"
                :src="src"
                muted
                playsinline
                crossorigin="anonymous"
                preload="auto"
                draggable="false"
                @loadedmetadata="buildFilmstrip"
                @error="onProbeError"
            ></video>

            <div class="tc-filmstrip" v-if="clip.sourceType === 'image'">
                <div class="tc-filmstrip-slot" v-for="n in tileCount" :key="n">
                    <img :src="src" draggable="false">
                </div>
            </div>
            <div class="tc-filmstrip tc-filmstrip-overlay" v-else-if="filmstrip.length" :style="filmstripOverlayStyle">
                <div class="tc-filmstrip-slot" v-for="(frame, i) in filmstrip" :key="i" :style="{ flex: `0 0 ${frameWidthPx}px`, width: frameWidthPx + 'px' }">
                    <img v-if="frame" :src="frame" draggable="false">
                    <i v-else-if="filmstripFailed" class="fa-solid fa-triangle-exclamation"></i>
                    <i v-else class="fa-solid fa-spinner fa-spin"></i>
                </div>
            </div>

            <!-- Silence-removal review (see MediaEditor.vue's silencePreview) - shown only while
                 actively reviewing this specific clip (previewSegments set), never for its own
                 committed state: once validated, cut portions are truly gone (ripple-deleted, see
                 getEffectiveClipDuration) and there's nothing left here to show a gap for. While
                 reviewing, the clip temporarily shows its *full*, pre-cut length instead (see
                 durationMs) so both kept and cut zones have real proportional space to occupy and
                 be told apart - kept zones toggle off (exclude) on click, cut zones toggle back on
                 (reintegrate); nothing is committed until "Valider" in ClipToolPanel. -->
            <template v-if="previewSegments">
                <div
                    v-for="zone in previewZones"
                    :key="zone.index"
                    class="tc-zone"
                    :class="zone.enabled ? 'tc-zone-kept' : 'tc-zone-cut'"
                    :style="{ left: zone.leftPct + '%', width: zone.widthPct + '%' }"
                    :title="$t(zone.enabled ? 'dash.post-creator.media-editor.clip-tool.segment-cut-tooltip' : 'dash.post-creator.media-editor.clip-tool.segment-restore-tooltip')"
                    @click.stop="$emit('toggle-preview-segment', { segmentIndex: zone.index, enabled: !zone.enabled })"
                >
                    <i class="fa-solid" :class="zone.enabled ? 'fa-scissors' : 'fa-rotate-left'"></i>
                </div>
                <div
                    v-for="boundary in previewBoundaries"
                    :key="'b' + boundary.index"
                    class="tc-zone-boundary"
                    :style="{ left: boundary.leftPct + '%' }"
                    @mousedown.stop.prevent="e => onBoundaryMouseDown(boundary.index, e)"
                ></div>
            </template>

            <!-- Only "trim the start" (video only - an image has no earlier source content to
                 reveal) needs the translateX compensation in previewStyle: shrinking the box from
                 the left, in a plain flex row, would otherwise only ever look like the *right*
                 edge moving (the box's own left edge can't move on its own in normal flow) -
                 leaving the handle visually stuck under the cursor the instant you started
                 dragging it; see onEdgeMouseDown/previewStyle.

                 .stop (not a Sortable filter/handle option - see onEdgeMouseDown's own comment on
                 why) is what keeps this from *also* starting a reorder-drag; .prevent on top of
                 it additionally blocks the browser's own native drag-ghost/text-selection
                 behavior a mousedown on a draggable-adjacent element can otherwise trigger. -->
            <div
                v-if="clip.sourceType === 'video'"
                class="tc-handle tc-handle-left"
                @mousedown.stop.prevent="e => onEdgeMouseDown('start', e)"
            ></div>
            <div class="tc-handle tc-handle-right" @mousedown.stop.prevent="e => onEdgeMouseDown('end', e)"></div>
        </div>

        <!-- Hover-revealed, straddles the gap after this clip - lets a new clip be inserted right
             here instead of only ever appendable at the very end of the strip. A single entry
             point (see AddClipMenu.vue) rather than two competing buttons, positioned above
             .tc-transition-chip below (which keeps its own existing spot) so both stay usable
             without overlapping. -->
        <div class="tc-insert-zone" v-if="!isLast" @click.stop>
            <AddClipMenu @pick-file="$emit('insert-file-after')" @pick-blank="color => $emit('insert-blank-after', color)" />
        </div>

        <!-- Editing a transition's type/direction/duration/easing now happens in the right-side
             ClipToolPanel (like every other clip tool - crop, speed, etc.) rather than a small
             floating popup here - a popup nested inside the timeline's own scrollable card had no
             good way to stay fully visible regardless of scroll/expand state, and a curve editor
             for the easing picker needs more room than a popup like that could reasonably give it
             anyway. The chip itself stays here as the trigger, just showing the current type. -->
        <div class="tc-transition-chip" v-if="!isLast" @click.stop="onTransitionChipClick">
            <i class="fa-solid" :class="transitionIcon"></i>
        </div>

        <div class="tc-info">
            <span class="tc-duration">{{ formatTimecode(durationMs) }}</span>
            <i class="fa-solid fa-trash tc-remove" @click.stop="$emit('remove')"></i>
        </div>
    </div>
</template>

<script>
import { getEffectiveClipDuration } from '../engine/timelineModel.js'
import { formatTimecode } from '../engine/time.js'
import { generateFilmstrip } from '../engine/thumbnails.js'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
import AddClipMenu from './AddClipMenu.vue'

// Roughly one sampled frame per this many rendered CSS pixels - keeps density consistent whether
// the clip box ends up narrow or (thanks to a long duration/flex-grow) very wide, instead of a
// fixed frame count that looks sparse when wide or wastefully dense when narrow.
const TARGET_FRAME_WIDTH_PX = 42
const MIN_FRAMES = 2
const MAX_FRAMES = 24
// A floor so a very short clip (or a heavily zoomed-out long video) never shrinks down to a
// sliver that's fiddly to see, click or drag.
const MIN_CLIP_WIDTH_PX = 60
// Regenerating a video filmstrip reseeks the whole thing - not worth doing on every intermediate
// resize tick, only once the size has actually settled.
const RESIZE_REGEN_DEBOUNCE_MS = 400
// Never trim a clip down to (near-)nothing via either handle.
const MIN_TRIM_MS = 200
// Same reasoning as TimelinePlayer.js's own media retry - a load failure here is more often a
// transient network blip than a truly broken URL, so a few silent retries first (see onProbeError)
// resolve most of those without needing the user to do anything.
const MAX_PROBE_RETRIES = 3
const PROBE_RETRY_DELAY_MS = 700
// How close to the scroll area's edge (px) the cursor needs to be to start auto-scrolling, and
// how fast (px per animation frame, scaled down the further the cursor is from the true edge).
const AUTO_SCROLL_EDGE_PX = 60
const AUTO_SCROLL_SPEED_PX = 14
// Icon shown on .tc-transition-chip for whichever transition type is currently set - the full
// list of options (with labels) lives in ClipToolPanel.vue's own "transition" tool now, this chip
// only ever needs to display the current pick, not edit it.
const TRANSITION_ICONS = {
    none: 'fa-ban',
    fade: 'fa-circle-half-stroke',
    wipe: 'fa-arrows-left-right',
    slide: 'fa-arrow-right',
    zoom: 'fa-magnifying-glass',
    blur: 'fa-droplet'
}

export default {
    name: 'TimelineClip',

    components: {
        Tooltip,
        AddClipMenu
    },

    props: {
        clip: {
            type: Object,
            required: true
        },
        src: {
            type: String,
            required: true
        },
        selected: {
            type: Boolean,
            default: false
        },
        isLast: {
            type: Boolean,
            default: false
        },
        pxPerSec: {
            type: Number,
            default: 100
        },
        // Non-null only for the one clip currently under silence-removal review (see
        // MediaEditor.vue's silencePreview) - an array of { startMs, endMs, enabled } spanning the
        // clip's *full* trimmed length (not yet ripple-deleted), same shape/units as clip.segments.
        previewSegments: {
            type: Array,
            default: null
        }
    },

    data() {
        return {
            filmstrip: [],
            filmstripRange: { startMs: 0, endMs: 0 },
            tileCount: MIN_FRAMES,
            // Bumped on every buildFilmstrip call - onFrame/isCancelled below compare against the
            // value captured at their own call's start, so a stale still-running generation (e.g.
            // a resize regen that started just before a trim regen also fired) stops writing
            // frames instead of racing the newer one and occasionally landing a wrong-timestamp
            // frame in a slot the newer call had already filled correctly.
            filmstripGeneration: 0,
            // Which trim handle (if any) is actively being dragged - see previewStyle/
            // onEdgeMouseDown. Only committed to a real trimStartMs/trimEndMs/imageDurationMs
            // update once the drag ends.
            draggingEdge: null,
            // Local-only preview of what the trim *would* commit to - not emitted to the parent
            // until release (see onEdgeMouseDown) - filmstripOverlayStyle reads this instead of
            // clip.trimStartMs while dragging, so the frame crop still animates smoothly with the
            // cursor even though the real clip data isn't updated yet.
            pendingTrim: null,
            // The filmstrip probe is its own separate <video> element (see buildFilmstrip's own
            // comment on why), hitting the same source URL as the main preview - just as likely to
            // hit a transient load failure, but with no error handling at all previously: it just
            // stayed on its loading spinner forever, even after the main preview had recovered.
            probeRetryCount: 0,
            filmstripFailed: false
        }
    },

    computed: {
        // While under silence-removal review, the box shows its *full* pre-cut length instead of
        // the normal ripple-deleted (segments-excluded) one - a shrunk box has no room left to
        // show what a cut portion even was, which is what silently broke this the first time
        // around (kept/cut zones were computed as fractions of the full span but rendered inside
        // a box already sized to the *shorter*, already-excluded-cuts span, landing them outside
        // its own visible bounds, or overlapping each other, instead of lining up with reality).
        durationMs() {
            if (this.previewSegments) {
                const totalSourceMs = this.clip.trimEndMs - this.clip.trimStartMs
                return totalSourceMs / (this.clip.playbackRate || 1)
            }
            return getEffectiveClipDuration(this.clip)
        },

        // Every segment while reviewing (both kept - click to exclude - and cut - click to
        // reintegrate - see the template), not just the cut ones the old, narrower version of
        // this only showed - positioned proportionally within the full trimmed window. Segment
        // startMs/endMs are already source-time relative to trimStartMs (same units
        // mapEffectiveMsToSourceMs uses), which lines up with durationMs above without needing any
        // playback-rate conversion of its own.
        previewZones() {
            if (!this.previewSegments || !this.previewSegments.length) return []
            const totalMs = Math.max(1, this.clip.trimEndMs - this.clip.trimStartMs)
            return this.previewSegments.map((seg, index) => ({
                index,
                enabled: seg.enabled,
                leftPct: (seg.startMs / totalMs) * 100,
                widthPct: ((seg.endMs - seg.startMs) / totalMs) * 100
            }))
        },

        // One draggable handle per *internal* boundary (between segment i and i+1) - not the
        // clip's own outer edges, which already have their own trim handles above.
        previewBoundaries() {
            if (!this.previewSegments || this.previewSegments.length < 2) return []
            const totalMs = Math.max(1, this.clip.trimEndMs - this.clip.trimStartMs)
            const boundaries = []
            for (let i = 0; i < this.previewSegments.length - 1; i++) {
                boundaries.push({ index: i, leftPct: (this.previewSegments[i].endMs / totalMs) * 100 })
            }
            return boundaries
        },

        // Clip width is duration x the timeline's own zoom level (px/sec), rather than a
        // flex-grow share of the available space - that's what lets the strip actually grow
        // wider than the visible area (and scroll) once zoomed in, instead of always being
        // squeezed to fit no matter how long the video or how many clips it has. A floor keeps
        // very short clips/images from shrinking down to an unreadable sliver.
        widthPx() {
            return Math.max(MIN_CLIP_WIDTH_PX, (this.durationMs / 1000) * this.pxPerSec)
        },

        // What pendingTrim would actually produce, in the same units as durationMs - deliberately
        // not the full segments-aware getEffectiveClipDuration (a trim-handle drag never has
        // silence-review segments in play at the same time), just enough to size the live preview
        // off the *real*, already-clamped trim values in pendingTrim rather than a separate,
        // independent px calculation that could disagree with them.
        pendingDurationMs() {
            if (!this.pendingTrim) return null
            if (this.clip.sourceType === 'image') return this.pendingTrim.imageDurationMs
            return (this.pendingTrim.trimEndMs - this.pendingTrim.trimStartMs) / (this.clip.playbackRate || 1)
        },

        // Live visual feedback while a handle is being dragged, entirely local to this component
        // (trimStartMs/trimEndMs/imageDurationMs only commit for real on release - see
        // onEdgeMouseDown). Sized directly off pendingTrim (via pendingDurationMs) rather than the
        // raw mouse-pixel delta - the previous version applied the raw delta to width (floored at
        // MIN_CLIP_WIDTH_PX) and separately to the translateX compensation *unclamped*, so once
        // the drag ran into computeTrim's own trimStartMs/trimEndMs bounds the two fell out of
        // sync: width stopped shrinking but the transform kept growing with the raw mouse
        // movement, dragging the box (and the handle sitting at its edge) further and further out
        // from under the cursor. Deriving both from the same pendingWidthPx fixed that clamp, but
        // MIN_CLIP_WIDTH_PX is its own *separate* clamp - only on the rendered width, deliberately
        // never applied to the shift below: a clip already at (or heavily trimmed down towards)
        // that floor has a rendered box that simply can't get any narrower, but the handle still
        // has to keep following the cursor past that point, using the *natural* (unfloored,
        // possibly sub-60px) width the box would have without the floor - using the *floored*
        // width for the shift too made the box (and its handle) look like it just stopped
        // responding entirely the instant the floor was reached, however far the cursor still had
        // to go. Dragging the *right* handle just grows/shrinks the box normally (a plain flex row
        // already handles that on its own). Dragging the *left* handle needs the translateX
        // compensation: shrinking width alone would only ever look like the box's right edge
        // moving (its own left edge can't move on its own within a flex row), so this shifts the
        // whole box right by the *natural* shrink amount, keeping the right edge anchored (unless
        // the floor is in play, in which case the right edge trails along too - a rare tradeoff,
        // preferred over the handle getting stuck) while the left edge follows the cursor.
        previewStyle() {
            if (!this.draggingEdge || this.pendingDurationMs === null) {
                return { width: this.widthPx + 'px' }
            }
            // The *before* reference has to be computed the exact same way as pendingDurationMs
            // (raw trimEnd-trimStart span, rate-adjusted - see pendingDurationMs's own comment on
            // why it deliberately ignores segments) rather than reusing this.durationMs, which
            // *does* account for enabled segments. For a clip that's had silence removed, those
            // two disagree - durationMs (segments-aware) is shorter than the raw span - which
            // otherwise made the box visibly jump the instant a drag started (before the cursor
            // had moved at all), since the "before" and "during" widths were computed two
            // different ways.
            const rate = this.clip.playbackRate || 1
            const naturalWidthPx = ((this.clip.trimEndMs - this.clip.trimStartMs) / rate / 1000) * this.pxPerSec
            const naturalPendingWidthPx = (this.pendingDurationMs / 1000) * this.pxPerSec
            const pendingWidthPx = Math.max(MIN_CLIP_WIDTH_PX, naturalPendingWidthPx)
            if (this.draggingEdge === 'start') {
                return { width: pendingWidthPx + 'px', transform: `translateX(${naturalWidthPx - naturalPendingWidthPx}px)` }
            }
            return { width: pendingWidthPx + 'px' }
        },

        // The chip only ever shows *which* transition is set (editing now happens in
        // ClipToolPanel's own "transition" tool, see onTransitionChipClick) - a plain lookup is
        // enough, no need for the full options list this component used to hold.
        transitionIcon() {
            return TRANSITION_ICONS[this.clip.transitionOut.type] || TRANSITION_ICONS.none
        },

        // Frame timestamps (filmstripRange) are all in *source* ms, but pxPerSec lays out
        // *effective* (post-playbackRate) display time - a sped-up clip packs more source ms into
        // the same on-screen px, so the mapping between the two needs the rate factored in too.
        pxPerSourceMs() {
            return this.pxPerSec / 1000 / (this.clip.playbackRate || 1)
        },

        // Fixed for as long as this.filmstrip itself is (i.e. until the next buildFilmstrip
        // regenerates it after the drag settles) - a fixed span of *source* time per frame, not a
        // flex-grow share of whatever's currently visible. This is what keeps already-sampled
        // frames a constant size while trimming, instead of every remaining one stretching to
        // fill the gap left by whichever frames just scrolled out of range.
        frameWidthPx() {
            const { startMs, endMs } = this.filmstripRange
            const frameDurationMs = (endMs - startMs) / Math.max(this.filmstrip.length, 1)
            return frameDurationMs * this.pxPerSourceMs
        },

        // The strip itself never resizes - it's shifted left by however far trimStartMs has moved
        // past the range it was actually sampled across (filmstripRange), so .tc-thumb's own
        // overflow:hidden crops whatever that reveals/hides on either side. That's what makes
        // frames *disappear* off the edge as a trim handle is dragged, rather than every visible
        // frame resizing to fill the shrinking box, which is what a flex/percentage-width strip
        // did before. Reads pendingTrim (this clip's own local, not-yet-committed drag state)
        // instead of clip.trimStartMs while actively dragging - update-trim itself isn't emitted
        // until release (see onEdgeMouseDown), so the real prop wouldn't move at all otherwise and
        // this crop would only ever jump once, at the end of the drag, instead of following the
        // cursor.
        filmstripOverlayStyle() {
            const liveTrimStartMs = this.pendingTrim ? this.pendingTrim.trimStartMs : this.clip.trimStartMs
            const offsetPx = (liveTrimStartMs - this.filmstripRange.startMs) * this.pxPerSourceMs
            return {
                width: (this.frameWidthPx * this.filmstrip.length) + 'px',
                transform: `translateX(${-offsetPx}px)`
            }
        }
    },

    mounted() {
        this.updateTileCountFromWidth()
        // The box's actual width depends on this clip's share of flex-grow among *all* clips -
        // that share changes whenever a clip is added/removed/reordered/trimmed elsewhere in the
        // timeline, not just from an actual window resize, so this has to stay live rather than
        // being measured once.
        this.resizeObserver = new ResizeObserver(() => this.updateTileCountFromWidth())
        this.resizeObserver.observe(this.$el)
    },

    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect()
        clearTimeout(this.regenTimer)
        cancelAnimationFrame(this.autoScrollRaf)
        // Stops any in-flight generateFilmstrip run from doing further pointless seek/decode work
        // (or writing into filmstrip) once this component is gone - see filmstripGeneration.
        this.filmstripGeneration++
    },

    methods: {
        formatTimecode,

        updateTileCountFromWidth() {
            const width = this.$el.getBoundingClientRect().width
            const next = Math.max(MIN_FRAMES, Math.min(MAX_FRAMES, Math.round(width / TARGET_FRAME_WIDTH_PX) || MIN_FRAMES))
            if (next === this.tileCount) return
            this.tileCount = next
            this.scheduleFilmstripRegen()
        },

        // Shared by both triggers (this clip's own box being resized/rezoomed, and its
        // trimStartMs/trimEndMs actually changing - see the watchers above) - debounced so a fast
        // drag doesn't re-seek through the whole video on every single tick, only once it settles.
        scheduleFilmstripRegen() {
            if (this.clip.sourceType !== 'video' || !this.filmstrip.length) return
            clearTimeout(this.regenTimer)
            this.regenTimer = setTimeout(this.buildFilmstrip, RESIZE_REGEN_DEBOUNCE_MS)
        },

        // Driven by the video's own @loadedmetadata in the template (same pattern as the proven,
        // already-shipped ThumbnailSelector.vue frame picker) rather than a video element created
        // and hidden off-screen in JS - that off-screen approach kept getting stuck showing only
        // a loader, most likely because browsers can deprioritize/throttle decoding for elements
        // they consider not actually visible (moved off-screen, shrunk to a few px, opacity:0…).
        // The video element is kept mounted for the component's whole lifetime (covered by the
        // filmstrip once it's ready, rather than removed) so a later resize can still reseek it.
        async buildFilmstrip() {
            if (this.clip.sourceType !== 'video' || !this.$refs.probe) return
            this.probeRetryCount = 0
            this.filmstripFailed = false
            // Pre-filled with empty slots right away (the overlay swaps in immediately, each slot
            // showing its own spinner) instead of only appearing once every single frame in the
            // whole clip has finished seeking - which is what made loading look like one giant,
            // slowly-changing frame the whole time instead of a strip filling in.
            this.filmstrip = new Array(this.tileCount).fill(null)
            // Snapshot of the range these particular frames were sampled across - frameWidthPx/
            // filmstripOverlayStyle need this (not the clip's own, possibly since-changed
            // trimStartMs/trimEndMs) to know what each already-generated frame's own timestamp
            // actually is.
            this.filmstripRange = { startMs: this.clip.trimStartMs, endMs: this.clip.trimEndMs }
            // See filmstripGeneration's own comment - this call's frames only ever get written
            // while it's still the *current* generation, so a superseded regen (another one
            // starting before this one finishes) can't clobber the newer one's results.
            const generation = ++this.filmstripGeneration
            await generateFilmstrip(this.$refs.probe, {
                startMs: this.clip.trimStartMs,
                endMs: this.clip.trimEndMs,
                count: this.tileCount,
                width: 90,
                isCancelled: () => this.filmstripGeneration !== generation,
                onFrame: (index, dataUrl) => {
                    if (this.filmstripGeneration !== generation) return
                    this.$set(this.filmstrip, index, dataUrl)
                }
            })
        },

        // Same URL as the main preview (TimelinePlayer.js), so just as likely to hit a transient
        // load failure - silently retries a few times (fresh src, same element) before giving up
        // and showing a warning icon in place of the spinner (see filmstripFailed in the template).
        onProbeError() {
            if (!this.$refs.probe) return
            const mediaError = this.$refs.probe.error
            console.error('[MediaEditor] timeline filmstrip probe failed to load', 'src=', this.src, 'code=', mediaError && mediaError.code, 'message=', mediaError && mediaError.message, 'retryCount=', this.probeRetryCount)
            if (this.probeRetryCount < MAX_PROBE_RETRIES) {
                this.probeRetryCount++
                setTimeout(() => {
                    if (!this.$refs.probe) return
                    this.$refs.probe.src = this.src
                    this.$refs.probe.load()
                }, PROBE_RETRY_DELAY_MS)
            } else {
                this.filmstripFailed = true
            }
        },

        // Drag-to-trim directly on the clip's own edges - "retirer/remettre une partie de vidéo"
        // (video: trimStartMs/trimEndMs) or a slideshow image's own duration, both now configured
        // only here instead of a separate tool-panel slider/number input (see also Timeline.vue's
        // clipTools and ClipToolPanel.vue, which no longer have a "trim" entry at all).
        //
        // Only emits update-trim once, on release - this used to commit (and so ripple a full
        // recomputeTimelineLayout/reflow through every other clip) on every single mousemove tick,
        // which fought visibly with this clip's own local preview (previewStyle/
        // filmstripOverlayStyle, both driven off pendingTrim below) updating at the
        // same time: the box/timeline duration visibly adjusting *while* the box itself was still
        // being dragged read as janky rather than one deliberate edit. Everything downstream
        // (other clips reflowing, the filmstrip's own re-sampled frames - see onUp's
        // scheduleFilmstripRegen) now only ever reacts once the drag has actually settled.
        onEdgeMouseDown(edge, e) {
            this.$emit('select')
            this.draggingEdge = edge
            this.pendingTrim = null
            const msPerPx = 1000 / this.pxPerSec
            const rate = this.clip.playbackRate || 1
            const isImage = this.clip.sourceType === 'image'
            const startX = e.clientX
            const originalTrimStart = this.clip.trimStartMs
            const originalTrimEnd = this.clip.trimEndMs
            const originalImageDuration = this.clip.imageDurationMs

            const scrollArea = this.$el.closest('.vt-scroll-area')
            const scrollLeftAtStart = scrollArea ? scrollArea.scrollLeft : 0
            let lastClientX = startX
            // Tracks the *intended* auto-scroll while held near/past an edge (see tickAutoScroll)
            // as its own running total, separate from the container's actual scrollLeft - which
            // the browser silently clamps at 0/max and can't exceed. The very first (or only)
            // clip has nothing before it to scroll into at all, so scrollLeft can never move past
            // 0 there no matter how long the edge is held - without a value that keeps growing
            // independently of what the browser will actually let scrollLeft do, restoring a
            // trimmed start past the visible edge on that clip was simply impossible, not just slow.
            let intendedScrollDelta = 0

            const deltaFromClientX = (clientX) => (clientX - startX) + intendedScrollDelta

            const computeTrim = (deltaPx) => {
                const deltaEffectiveMs = deltaPx * msPerPx

                if (isImage) {
                    // Images have no trimStart concept (the whole picture is always shown) - only
                    // their own duration (both handles resize it the same way, since there's no
                    // "earlier source content" a left-edge drag could ever reveal).
                    const newDuration = Math.max(MIN_TRIM_MS, originalImageDuration + deltaEffectiveMs * (edge === 'start' ? -1 : 1))
                    return { imageDurationMs: Math.round(newDuration), trimEndMs: Math.round(newDuration) }
                }

                const deltaSourceMs = deltaEffectiveMs * rate
                if (edge === 'start') {
                    const newStart = Math.min(Math.max(0, originalTrimStart + deltaSourceMs), originalTrimEnd - MIN_TRIM_MS)
                    return { trimStartMs: Math.round(newStart), trimEndMs: originalTrimEnd }
                }
                const newEnd = Math.max(Math.min(this.clip.sourceDurationMs, originalTrimEnd + deltaSourceMs), originalTrimStart + MIN_TRIM_MS)
                return { trimStartMs: originalTrimStart, trimEndMs: Math.round(newEnd) }
            }

            // Local-only, every tick - drives previewStyle (box size) and filmstripOverlayStyle
            // (frame crop) so both keep animating smoothly with the cursor even though nothing is
            // emitted to the real timeline model until release.
            const updatePreview = (deltaPx) => {
                this.pendingTrim = computeTrim(deltaPx)
            }

            // Auto-scrolls the timeline while the cursor is held near/past its visible edge, so
            // extending a duration (or restoring a trimmed start) past what's currently on screen
            // doesn't leave the user stuck making only tiny adjustments right at the scroll
            // boundary - matches how dragging near an edge behaves in most editors/file managers.
            // See intendedScrollDelta above for why this doesn't just read back scrollArea's own
            // (browser-clamped) scrollLeft.
            //
            // Gated on hasMoved: the handle itself can easily already sit within the edge
            // threshold at the moment of mousedown (e.g. the last clip's right handle, right at
            // the edge of a fully-zoomed-out timeline) - without requiring at least one real
            // mousemove first, that started the auto-scroll (and kept committing trim changes)
            // immediately on click, before the user had dragged at all.
            let hasMoved = false
            const tickAutoScroll = () => {
                if (!this.draggingEdge || !scrollArea) return
                if (!hasMoved) { this.autoScrollRaf = requestAnimationFrame(tickAutoScroll); return }
                const rect = scrollArea.getBoundingClientRect()
                const distanceFromRight = rect.right - lastClientX
                const distanceFromLeft = lastClientX - rect.left
                // Past the edge (negative distance) ramps up well beyond the base speed instead
                // of capping at it - restoring a long-trimmed portion by holding just past the
                // edge was unusably slow otherwise (a few px/frame right at the boundary, only
                // ever reaching the base speed and no further).
                let speed
                if (distanceFromRight < AUTO_SCROLL_EDGE_PX) {
                    speed = AUTO_SCROLL_SPEED_PX * (1 + Math.max(0, AUTO_SCROLL_EDGE_PX - distanceFromRight) / 10)
                } else if (distanceFromLeft < AUTO_SCROLL_EDGE_PX) {
                    speed = -AUTO_SCROLL_SPEED_PX * (1 + Math.max(0, AUTO_SCROLL_EDGE_PX - distanceFromLeft) / 10)
                } else {
                    this.autoScrollRaf = requestAnimationFrame(tickAutoScroll)
                    return
                }
                // Clamped to what the browser can actually apply to scrollLeft (0..max), not left
                // to grow unbounded - it used to keep accumulating forever once scrollLeft hit its
                // real floor/ceiling (e.g. the very first clip, already at trimStart 0, with
                // nothing before it to scroll into at all), building up a huge hidden "debt" in
                // deltaFromClientX below that then had to be paid off by real mouse movement in
                // the *opposite* direction before the trim calculation responded to the cursor
                // again at all - that's what made a first drag on the timeline's very first clip
                // look completely frozen (pinned at its own 0 clamp) until the cursor had moved
                // most of the way across the screen.
                intendedScrollDelta += speed
                const maxScrollLeft = Math.max(0, scrollArea.scrollWidth - scrollArea.clientWidth)
                intendedScrollDelta = Math.max(-scrollLeftAtStart, Math.min(intendedScrollDelta, maxScrollLeft - scrollLeftAtStart))
                scrollArea.scrollLeft = scrollLeftAtStart + intendedScrollDelta
                updatePreview(deltaFromClientX(lastClientX))
                this.autoScrollRaf = requestAnimationFrame(tickAutoScroll)
            }
            this.autoScrollRaf = requestAnimationFrame(tickAutoScroll)

            const onMove = (moveEvent) => {
                hasMoved = true
                lastClientX = moveEvent.clientX
                updatePreview(deltaFromClientX(moveEvent.clientX))
            }

            // Emits the real update-trim exactly once, from the mouseup's own coordinates - a
            // fast, tiny release-without-movement can land with zero mousemove events ever firing
            // in between (the browser coalesces/drops them), so this always recomputes from
            // upEvent itself rather than trusting pendingTrim to already be set.
            const onUp = (upEvent) => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
                cancelAnimationFrame(this.autoScrollRaf)
                this.$emit('update-trim', computeTrim(deltaFromClientX(upEvent.clientX)))
                this.draggingEdge = null
                this.pendingTrim = null
                // Only re-samples real frames now that the drag has actually settled, not on
                // every intermediate tick.
                this.scheduleFilmstripRegen()
            }

            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },

        // Retimes a cut directly by dragging the boundary between two adjacent preview segments -
        // "modifier les curseurs" - rather than only being able to toggle a whole segment on/off.
        // msPerPx is derived from durationMs/widthPx (both already accounting for the full,
        // pre-cut span in preview mode - see their own comments) rather than querying the DOM.
        onBoundaryMouseDown(boundaryIndex, e) {
            this.$emit('select')
            const before = this.previewSegments[boundaryIndex]
            const after = this.previewSegments[boundaryIndex + 1]
            if (!before || !after) return

            const startX = e.clientX
            const originalAtMs = before.endMs
            const minMs = before.startMs + MIN_TRIM_MS
            const maxMs = after.endMs - MIN_TRIM_MS
            const msPerPx = (this.clip.trimEndMs - this.clip.trimStartMs) / this.widthPx

            const onMove = (moveEvent) => {
                const deltaMs = (moveEvent.clientX - startX) * msPerPx
                const atMs = Math.min(Math.max(originalAtMs + deltaMs, minMs), maxMs)
                this.$emit('move-preview-boundary', { boundaryIndex, atMs: Math.round(atMs) })
            }
            const onUp = () => {
                document.removeEventListener('mousemove', onMove)
                document.removeEventListener('mouseup', onUp)
            }
            document.addEventListener('mousemove', onMove)
            document.addEventListener('mouseup', onUp)
        },

        // Opens the transition editor in the right-side ClipToolPanel for *this* clip specifically
        // - 'select' first (so the tool panel operates on the clip the chip actually belongs to,
        // not whichever one might already be selected or under the playhead) then the tool
        // request itself, both synchronous so selectedClipId is already updated by the time
        // MediaEditor's onSelectClipTool runs its own fallback check.
        onTransitionChipClick() {
            this.$emit('select')
            this.$emit('select-clip-tool')
        }
    }
}
</script>

<style lang="scss" scoped></style>
